import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminScalpingSettings.css';
import AdminScalpingLab from './AdminScalpingLab';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const fmt = (n, d = 0) =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });

/** Seconds since last sampler tick (Infinity if unknown). */
function tickAgeSec(lastTickAt) {
  if (!lastTickAt) return Infinity;
  const raw = String(lastTickAt);
  const parsed = Date.parse(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
  return Number.isFinite(parsed) ? (Date.now() - parsed) / 1000 : Infinity;
}

/** Only surface feed errors when the spine is truly unhealthy (not a reconnect blip). */
function showFeedError(g) {
  if (!g?.lastError) return false;
  const ws = (g.wsStatus || '').toLowerCase();
  if (ws === 'error') return true;
  if (!g.enabled || ws === 'idle') return false;
  return tickAgeSec(g.lastTickAt) > 120;
}

// Editable numeric settings grouped by purpose -> [key, label, hint]
const PARAM_GROUPS = [
  {
    title: 'Strategy (deviation bands)',
    fields: [
      ['entryDevBps', 'Entry deviation (bps)', 'Fade only dislocations this large'],
      ['exitDevBps', 'Exit deviation (bps)', 'Take profit when reverted inside this'],
      ['stopDevBps', 'Stop deviation (bps)', 'Hard stop if dislocation widens past this'],
      ['maxHoldMs', 'Max hold (ms)', 'Time-stop if no reversion'],
      ['entryTimeoutMs', 'Entry fill timeout (ms)', 'Cancel maker entry if unfilled'],
    ],
  },
  {
    title: 'Liquidity gates & sizing',
    fields: [
      ['sampleIntervalMs', 'Sample interval (ms)', 'Snapshot cadence per symbol'],
      ['maxSpreadBps', 'Max spread (bps)', 'Skip if spread wider than this'],
      ['minTopDepthUsd', 'Min top depth (USD)', 'Skip if book too thin'],
      ['maxLeverage', 'Max leverage', 'Hard cap per trade'],
      ['defaultNotionalUsd', 'Default margin (USD)', 'Collateral per trade before leverage'],
      ['defaultLeverageCrypto', 'Default crypto leverage', 'Backtest / paper position multiplier (e.g. 5x)'],
      ['defaultLeverageHip3', 'Default HIP-3 leverage', 'Backtest / paper position multiplier (e.g. 3x)'],
    ],
  },
  {
    title: 'Costs & monetization',
    fields: [
      ['makerFeeBps', 'Maker fee (bps)', 'Crypto base; HIP-3 = x multiplier'],
      ['takerFeeBps', 'Taker fee (bps)', 'Used on stop / timeout exits'],
      ['hip3FeeMultiplier', 'HIP-3 fee multiplier', 'Tokenized-stock fee factor (2x)'],
      ['commissionBpsPerSide', 'Commission (bps/side)', 'Platform fee via builder code'],
      ['profitSharePct', 'Profit share (%)', 'On net realized PnL'],
    ],
  },
];
const NUMERIC_FIELDS = PARAM_GROUPS.flatMap((grp) => grp.fields);

export default function AdminScalpingSettings() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/scalping/status`, { headers: authHeaders(), timeout: 45000 });
      setStatus(data);
      setForm((f) => (Object.keys(f).length ? f : { ...data.global }));
      setErr(null);
    } catch (e) {
      // Harmless while synthetic backtest blocks the API process (nginx 408).
      if (e.response?.status === 408 || e.code === 'ECONNABORTED') return;
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const patch = async (payload, note) => {
    setSaving(true);
    setMsg(null);
    setErr(null);
    try {
      const { data } = await axios.patch(`${API_BASE}/scalping/admin/config`, payload, { headers: authHeaders() });
      setForm((f) => ({ ...f, ...data.global }));
      setMsg(note || 'Saved');
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!status && loading) {
    return (
      <div className="ascalp">
        <div className="ascalp-head">
          <div>
            <h2>⚡ Scalping (OAS) Engine</h2>
            <p className="ascalp-sub">Loading engine status…</p>
          </div>
        </div>
        <AdminScalpingLab deferLoad />
      </div>
    );
  }

  const g = status?.global || {};
  const rec = status?.recording || {};
  const signals = status?.signals || [];
  const wsOk = ['open', 'degraded'].includes((g.wsStatus || '').toLowerCase());
  const wsWarn = ['connecting', 'reconnecting'].includes((g.wsStatus || '').toLowerCase());

  return (
    <div className="ascalp">
      <div className="ascalp-head">
        <div>
          <h2>⚡ Scalping (OAS) Engine</h2>
          <p className="ascalp-sub">Oracle-Anchored Adaptive Scalper — data spine + measurement (oBot, paper phase)</p>
        </div>
        <div className="ascalp-master">
          <span className={`ascalp-dot ${g.enabled ? 'on' : 'off'}`} />
          <button
            className={`ascalp-bigtoggle ${g.enabled ? 'on' : ''}`}
            disabled={saving}
            onClick={() => patch({ enabled: !g.enabled }, g.enabled ? 'Engine disabled' : 'Engine enabled — spine starting')}
          >
            {g.enabled ? 'ENGINE ON' : 'ENGINE OFF'}
          </button>
        </div>
      </div>

      {err && <div className="ascalp-err">{err}</div>}
      {msg && <div className="ascalp-ok">{msg}</div>}
      {showFeedError(g) && (
        <div className="ascalp-err">Feed error: {g.lastError}</div>
      )}
      {wsWarn && g.enabled && !showFeedError(g) && (
        <div className="ascalp-ok">Feed reconnecting — ticks should resume shortly.</div>
      )}

      <div className="ascalp-statusbar">
        <div><span>Feed</span><strong className={wsOk ? 'good' : wsWarn ? 'warn' : 'bad'}>{g.wsStatus || (loading ? '…' : '—')}</strong></div>
        <div><span>Last tick</span><strong>{g.lastTickAt ? new Date(g.lastTickAt).toLocaleTimeString() : '—'}</strong></div>
        <div><span>Snapshots</span><strong>{loading && !rec.snapshots ? '…' : fmt(rec.snapshots)}</strong></div>
        <div><span>Trades</span><strong>{loading && !rec.trades ? '…' : fmt(rec.trades)}</strong></div>
        <div><span>Since</span><strong>{rec.sinceTs ? new Date(rec.sinceTs).toLocaleString() : '—'}</strong></div>
      </div>

      <div className="ascalp-profiles">
        <div className="ascalp-profile crypto">
          <h4>OAS-Crypto — <strong>SOL live paper</strong> · <strong>BTC+ETH backtest</strong> · matrix all crypto</h4>
          <ul>
            <li><strong>SOL:</strong> v34 — entry 20 · stale 70s · PnL bleed 10s/−5 bps · live paper</li>
            <li><strong>BTC:</strong> v34 — stale 45s maker · PnL bleed 10s/−5 bps · backtest + matrix</li>
            <li><strong>ETH:</strong> v34 — entry 21 · stale 60s maker · PnL bleed 10s/−5 bps · backtest + matrix</li>
            <li>Add HIP-3 to matrix when hip3 trading enabled — synthetic first, recorded passive</li>
            <li>Regression gate: recorded 72h (Realistic synthetic headline unreliable until generator rewrite)</li>
            <li>TP: 70% dislocation reversion · bleed on touch gross ≤ −5 bps after 10s (no dev-widen gate)</li>
            <li>Timeout exits at maker mid when oracle reverted (avoids taker bleed)</li>
            <li>Commission {g.cryptoProfile?.commissionBpsPerSide ?? 0.25} bps/side (A1)</li>
          </ul>
        </div>
        <div className={`ascalp-profile hip3 ${g.hip3TradingEnabled ? '' : 'dim'}`}>
          <h4>OAS-HIP3 (stocks / index / commodities) {!g.hip3TradingEnabled && <span className="ascalp-badge">sidelined</span>}</h4>
          <ul>
            {!g.hip3TradingEnabled && <li><strong>Trading off</strong> — recording continues until crypto is net-positive</li>}
            <li>Entry {g.hip3Profile?.entryDevBps ?? 11} bps · Exit {g.hip3Profile?.exitDevBps ?? 2} bps · Max hold {((g.hip3Profile?.maxHoldMs ?? 120000) / 1000).toFixed(0)}s</li>
            <li>NVDA override: entry 14 bps base (+ session bumps off-hours)</li>
            <li>Commission {g.hip3Profile?.commissionBpsPerSide ?? 0.5} bps/side · Imbalance confirm on</li>
            <li>24/7/365 — never off. Cash hrs active · weekends reduced · night 10pm–5am ET limited</li>
          </ul>
        </div>
      </div>

      <div className="ascalp-toggles">
        <label className="ascalp-switch">
          <input type="checkbox" checked={!!g.recordMarketData} disabled={saving}
            onChange={() => patch({ recordMarketData: !g.recordMarketData })} />
          <span>Record market data</span>
        </label>
        <label className="ascalp-switch">
          <input type="checkbox" checked={!!g.paperMode} disabled={saving}
            onChange={() => patch({ paperMode: !g.paperMode })} />
          <span>Paper mode {g.paperMode ? '' : '(LIVE)'}</span>
        </label>
        <label className="ascalp-switch">
          <input type="checkbox" checked={!!g.makerOnly} disabled={saving}
            onChange={() => patch({ makerOnly: !g.makerOnly })} />
          <span>Maker-only entries</span>
        </label>
        <label className="ascalp-switch">
          <input type="checkbox" checked={!!g.paperTradingEnabled} disabled={saving}
            onChange={() => patch({ paperTradingEnabled: !g.paperTradingEnabled }, g.paperTradingEnabled ? 'Live paper trading off' : 'Live paper trading on')} />
          <span>Live paper trading {g.paperTradingEnabled ? '(recording trades)' : ''}</span>
        </label>
        <label className="ascalp-switch">
          <input type="checkbox" checked={!!g.hip3TradingEnabled} disabled={saving}
            onChange={() => patch({ hip3TradingEnabled: !g.hip3TradingEnabled }, g.hip3TradingEnabled ? 'HIP-3 trading off' : 'HIP-3 trading on')} />
          <span>HIP-3 trading {g.hip3TradingEnabled ? '(live + backtest)' : '(sidelined — data only)'}</span>
        </label>
      </div>

      {PARAM_GROUPS.map((grp) => (
        <div className="ascalp-paramgroup" key={grp.title}>
          <h4>{grp.title}</h4>
          <div className="ascalp-grid">
            {grp.fields.map(([key, label, hint]) => (
              <div className="ascalp-field" key={key}>
                <label>{label}</label>
                <input
                  type="number"
                  value={form[key] ?? ''}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
                <small>{hint}</small>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="ascalp-actions">
        <button
          className="ascalp-save"
          disabled={saving}
          onClick={() => {
            const payload = {};
            for (const [key] of NUMERIC_FIELDS) if (form[key] !== '' && form[key] != null) payload[key] = Number(form[key]);
            patch(payload, 'Parameters saved');
          }}
        >
          {saving ? 'Saving…' : 'Save parameters'}
        </button>
      </div>

      <div className="ascalp-signals">
        <h3>Live oracle-deviation signal</h3>
        <table>
          <thead>
            <tr><th>Symbol</th><th>Mid</th><th>Oracle</th><th>Dev (bps)</th><th>Spread</th><th>Bias</th><th>Tradeable</th></tr>
          </thead>
          <tbody>
            {signals.map((s) => (
              <tr key={s.symbol}>
                <td><strong>{s.symbol}</strong> <span className="cat">{s.category}</span></td>
                <td>{s.mid == null ? '—' : fmt(s.mid, 4)}</td>
                <td>{s.oraclePx == null ? '—' : fmt(s.oraclePx, 4)}</td>
                <td className={s.devBps > 0 ? 'pos' : s.devBps < 0 ? 'neg' : ''}>
                  {s.devBps == null ? '—' : `${s.devBps >= 0 ? '+' : ''}${s.devBps.toFixed(1)}`}
                </td>
                <td>{s.spreadBps == null ? '—' : `${s.spreadBps.toFixed(1)}`}</td>
                <td>{s.side ? <span className={`side ${s.side}`}>{s.side}</span> : '—'}</td>
                <td>{s.tradeable ? <span className="yes">●</span> : <span className="no">○</span>}</td>
              </tr>
            ))}
            {!signals.length && <tr><td colSpan={7} className="empty">Waiting for market data…</td></tr>}
          </tbody>
        </table>
      </div>

      <AdminScalpingLab deferLoad />
    </div>
  );
}
